import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../context/ThemeContext'
import { 
  HiClipboardList, 
  HiUserGroup, 
  HiLightningBolt, 
  HiChat,
  HiShieldCheck,
  HiArrowRight,
  HiSun,
  HiMoon,
} from 'react-icons/hi'

const FEATURES = [
  {
    icon: HiClipboardList,
    title: 'Kanban Boards',
    desc: 'Visual project boards with drag-and-drop task management, custom columns, and real-time status tracking.',
  },
  {
    icon: HiLightningBolt,
    title: 'HAL 9000',
    desc: 'Your AI project manager. Creates tasks, tracks dependencies, processes meeting notes, and keeps everything moving. It\'s from Portland.',
  },
  {
    icon: HiUserGroup,
    title: 'Team Collaboration',
    desc: 'Assign tasks, comment on work, set reminders, and keep your whole team aligned across departments.',
  },
  {
    icon: HiChat,
    title: 'Smart Integrations',
    desc: 'Microsoft Teams notifications, SharePoint imports, calendar sync, and transcript processing built in.',
  },
]

function BrandMark({ className = 'w-11 h-11', textClassName = 'text-sm' }) {
  return (
    <div className={`${className} rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20`}>
      <span className={`${textClassName} font-black text-slate-950 tracking-tight`}>S&amp;S</span>
    </div>
  )
}

export default function LoginPage() {
  const { login } = useAuth()
  const { effectiveTheme, setTheme } = useTheme()
  const [loaded, setLoaded] = useState(false)
  const isDark = effectiveTheme === 'dark'

  const toggleTheme = () => setTheme(isDark ? 'light' : 'dark')

  useEffect(() => {
    // Trigger entrance animations
    const t = setTimeout(() => setLoaded(true), 50)
    return () => clearTimeout(t)
  }, [])

  return (
    <div className={`min-h-dvh flex flex-col ${isDark ? 'bg-slate-950' : 'bg-white'}`}>
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className={`fixed top-5 right-5 z-50 p-2.5 rounded-xl border backdrop-blur-sm transition-all duration-200 ${
          isDark
            ? 'border-white/10 bg-white/5 text-slate-400 hover:text-amber-400 hover:bg-white/10'
            : 'border-surface-200 bg-white/80 text-surface-500 hover:text-amber-500 hover:bg-surface-50 shadow-sm'
        }`}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? <HiSun className="w-5 h-5" /> : <HiMoon className="w-5 h-5" />}
      </button>

      {/* ── Decorative background ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orbs */}
        <div className={`absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-[120px] ${isDark ? 'bg-amber-500/10' : 'bg-amber-400/15'}`} />
        <div className={`absolute top-1/2 -left-40 w-[500px] h-[500px] rounded-full blur-[100px] ${isDark ? 'bg-brand-500/8' : 'bg-brand-400/10'}`} />
        <div className={`absolute -bottom-40 right-1/3 w-[400px] h-[400px] rounded-full blur-[100px] ${isDark ? 'bg-amber-400/6' : 'bg-amber-300/10'}`} />
        {/* Subtle grid overlay */}
        <div className={`absolute inset-0 ${isDark ? 'opacity-[0.03]' : 'opacity-[0.04]'}`} style={{
          backgroundImage: `linear-gradient(${isDark ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.06)'} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? 'rgba(255,255,255,.1)' : 'rgba(0,0,0,.06)'} 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
        }} />
      </div>

      {/* ── Main Content ── */}
      <div className="relative z-10 flex min-h-dvh flex-col lg:flex-row">

        {/* ── Left Panel — Hero / Branding ── */}
        <div className="hidden lg:flex flex-1 flex-col justify-center px-20 py-20">
          {/* Logo */}
          <div className={`transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center gap-3 mb-12">
              <BrandMark />
              <span className={`text-sm font-medium tracking-wide uppercase ${isDark ? 'text-white/60' : 'text-surface-500'}`}>Salt & Straw</span>
            </div>
          </div>

          {/* Headline */}
          <div className={`transition-all duration-700 delay-100 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight mb-6 ${isDark ? 'text-white' : 'text-surface-900'}`}>
              Projects, managed
              <br />
              <span className="bg-gradient-to-r from-amber-500 to-amber-400 bg-clip-text text-transparent">
                with intention.
              </span>
            </h1>
          </div>

          <div className={`transition-all duration-700 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <p className={`text-lg max-w-lg leading-relaxed mb-10 ${isDark ? 'text-slate-400' : 'text-surface-500'}`}>
              The internal project tracker built for Salt & Straw teams. Plan work, track progress, 
              manage dependencies, and let AI handle the busywork — so you can focus on what matters.
            </p>
          </div>

          {/* Feature cards */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl transition-all duration-700 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {FEATURES.map((feature) => (
              <div
                key={feature.title}
                className={`group p-4 rounded-xl border transition-all duration-300 ${
                  isDark
                    ? 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.1]'
                    : 'border-surface-200 bg-surface-50/50 hover:bg-surface-100 hover:border-surface-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg shrink-0 transition-colors ${
                    isDark
                      ? 'bg-amber-500/10 text-amber-400 group-hover:bg-amber-500/15'
                      : 'bg-amber-100 text-amber-600 group-hover:bg-amber-200'
                  }`}>
                    <feature.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={`text-sm font-semibold mb-1 ${isDark ? 'text-white' : 'text-surface-800'}`}>{feature.title}</h3>
                    <p className={`text-xs leading-relaxed ${isDark ? 'text-slate-500' : 'text-surface-500'}`}>{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Right Panel — Sign-in Card ── */}
        <div className="min-h-dvh lg:min-h-0 lg:w-[480px] xl:w-[520px] flex items-center justify-center px-5 sm:px-8 py-16 lg:py-20">
          <div className={`w-full max-w-sm transition-all duration-700 delay-500 ${loaded ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-[0.97]'}`}>
            <div className={`backdrop-blur-xl rounded-2xl p-6 sm:p-8 shadow-2xl ${
              isDark
                ? 'bg-white/[0.04] border border-white/[0.08] shadow-black/20'
                : 'bg-white border border-surface-200 shadow-surface-200/50'
            }`}>
              {/* Card logo */}
              <div className="flex flex-col items-center gap-3 mb-8">
                <BrandMark className="w-16 h-16" textClassName="text-lg" />
                <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-surface-500'}`}>
                  Salt &amp; Straw Project Tracker
                </p>
              </div>

              <h2 className={`text-xl font-bold text-center mb-2 ${isDark ? 'text-white' : 'text-surface-800'}`}>
                Welcome back
              </h2>
              <p className={`text-sm text-center mb-8 ${isDark ? 'text-slate-400' : 'text-surface-500'}`}>
                Sign in with your Microsoft account to access your projects and tasks.
              </p>

              {/* Sign in button */}
              <button
                onClick={login}
                className={`w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl font-semibold text-sm active:scale-[0.98] transition-all duration-200 group ${
                  isDark
                    ? 'bg-white text-slate-900 hover:bg-slate-100 active:bg-slate-200 shadow-lg shadow-white/10 hover:shadow-white/20'
                    : 'bg-surface-900 text-white hover:bg-surface-800 active:bg-surface-700 shadow-lg shadow-surface-900/20 hover:shadow-surface-900/30'
                }`}
              >
                {/* Microsoft logo */}
                <svg className="w-5 h-5 shrink-0" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                Sign in with Microsoft
                <HiArrowRight className={`w-4 h-4 group-hover:translate-x-0.5 transition-transform ${isDark ? 'text-slate-500' : 'text-white/60'}`} />
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 my-6">
                <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-surface-200'}`} />
                <span className={`text-xs font-medium ${isDark ? 'text-slate-600' : 'text-surface-400'}`}>INTERNAL TOOL</span>
                <div className={`flex-1 h-px ${isDark ? 'bg-white/[0.06]' : 'bg-surface-200'}`} />
              </div>

              {/* Security note */}
              <div className={`flex items-start gap-2.5 ${isDark ? 'text-slate-500' : 'text-surface-500'}`}>
                <HiShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-green-500/70" />
                <p className="text-xs leading-relaxed">
                  Secured with Microsoft Entra ID. Only Salt & Straw team members with an organizational account can sign in.
                </p>
              </div>
            </div>

            {/* Footer */}
            <p className={`text-center text-xs mt-6 ${isDark ? 'text-slate-600' : 'text-surface-400'}`}>
              &copy; {new Date().getFullYear()} Salt & Straw &middot; Project Tracker v2.0
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
